import { z } from 'zod';
import { LOCAL_ROLES } from './local-ledger';
/**
 * Roles a superadmin grants from the admin client card
 * (PUT /api/admin/users/:userId/roles). Every OTHER role a user holds
 * (`user`, `dealer` — owned by the dealer state machine —, `staff`, anything
 * unknown) is NOT managed by that endpoint and survives a change untouched.
 *
 * Canonical order: the api writes the managed part of `users.role` in exactly
 * this order, after the unmanaged roles.
 */
export const MANAGED_ROLES = ['admin', 'superadmin', ...LOCAL_ROLES];
export const ManagedRoleSchema = z.enum(MANAGED_ROLES);
/**
 * Body of PUT /api/admin/users/:userId/roles — the FULL set of managed roles
 * the user should hold afterwards. Invariants:
 *  - `superadmin` ⇒ `admin` (additive owner role: a superuser also holds admin);
 *  - at most one `local_*` role.
 */
export const UserRolesPutSchema = z
    .object({ roles: z.array(ManagedRoleSchema).max(MANAGED_ROLES.length) })
    .strict()
    .refine((body) => !body.roles.includes('superadmin') || body.roles.includes('admin'), {
    message: 'superadmin requires admin',
    path: ['roles'],
})
    .refine((body) => body.roles.filter((role) => role.startsWith('local_')).length <= 1, {
    message: 'at most one local_* role',
    path: ['roles'],
});
/**
 * One row of `user_role_journal` — append-only, written in the same
 * transaction as the `users.role` update. `previous`/`next` are the FULL role
 * arrays (managed and unmanaged), `changedBy` the acting superadmin's id.
 */
export const UserRoleJournalEntrySchema = z.object({
    userId: z.string(),
    previous: z.array(z.string()),
    next: z.array(z.string()),
    changedBy: z.string(),
    changedAt: z.coerce.date(),
});
/** Result of a successful PUT: the role array before and after. */
export const UserRolesChangeSchema = z.object({
    previous: z.array(z.string()),
    next: z.array(z.string()),
});
//# sourceMappingURL=user-roles.js.map